/**
 * Bottom dock under chart — MT5 Toolbox style.
 * Right-click the panel (not a toolbar button) for Positions / Deals / Orders,
 * Period, and Columns. Trade view = open positions + pending.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import History from "views/pages/History";
import PositionReport from "views/pages/Positionreport";
import "assets/css/dock-terminal.css";

/** History menu items (MT5 History toolbox). Trade/open positions is separate via Trade icon. */
const HISTORY_VIEWS = [
  { id: "closed", label: "Positions" },
  { id: "deals", label: "Deals" },
  { id: "orders", label: "Orders" },
];

const PERIODS = [
  { id: "today", label: "Today", days: 0 },
  { id: "week", label: "Last week", days: 7 },
  { id: "month", label: "Last Month", months: 1 },
  { id: "months3", label: "Last 3 Months", months: 3 },
  { id: "months6", label: "Last 6 Months", months: 6 },
  { id: "year", label: "Last year", months: 12 },
  { id: "all", label: "All History", days: null },
];

/** Column defs per history tab — must match full-page History tables. */
const COLS_BY_TAB = {
  deals: [
    { id: "time", label: "Time", defaultOn: true },
    { id: "type", label: "Type", defaultOn: true },
    { id: "symbol", label: "Symbol", defaultOn: true },
    { id: "entry", label: "Entry", defaultOn: true },
    { id: "lots", label: "Lots", defaultOn: true },
    { id: "qty", label: "Qty", defaultOn: true },
    { id: "price", label: "Price", defaultOn: true },
    { id: "commission", label: "Commission", defaultOn: true },
    { id: "profit", label: "Profit", defaultOn: true },
    { id: "positionId", label: "Position#", defaultOn: true },
    { id: "orderId", label: "Order#", defaultOn: true },
  ],
  orders: [
    { id: "setup", label: "Setup", defaultOn: true },
    { id: "done", label: "Done", defaultOn: true },
    { id: "state", label: "State", defaultOn: true },
    { id: "side", label: "Buy/Sell", defaultOn: true },
    { id: "type", label: "Type", defaultOn: true },
    { id: "symbol", label: "Symbol", defaultOn: true },
    { id: "lots", label: "Lots", defaultOn: true },
    { id: "qty", label: "Qty", defaultOn: true },
    { id: "price", label: "Price", defaultOn: true },
  ],
  closed: [
    { id: "open", label: "Open", defaultOn: true },
    { id: "close", label: "Close", defaultOn: true },
    { id: "type", label: "Type", defaultOn: true },
    { id: "symbol", label: "Symbol", defaultOn: true },
    { id: "lots", label: "Lots", defaultOn: true },
    { id: "qty", label: "Qty", defaultOn: true },
    { id: "openPrice", label: "Open Price", defaultOn: true },
    { id: "closePrice", label: "Close Price", defaultOn: true },
    { id: "commission", label: "Commission", defaultOn: true },
    { id: "profit", label: "Profit", defaultOn: true },
    { id: "positionId", label: "Position#", defaultOn: true },
  ],
};

/** v2 — tab-scoped keys aligned with full-page History (invalidates old Direction/etc.). */
const COLS_STORAGE = "fx_dock_hist_cols_v2";
const PERIOD_STORAGE = "fx_dock_hist_period_v1";

function defaultColMap(tab) {
  const map = {};
  (COLS_BY_TAB[tab] || []).forEach((c) => {
    map[c.id] = c.defaultOn;
  });
  return map;
}

function loadCols(tab) {
  const defaults = defaultColMap(tab);
  try {
    const raw = JSON.parse(localStorage.getItem(COLS_STORAGE) || "{}");
    const saved = raw[tab];
    if (saved && typeof saved === "object") {
      const map = { ...defaults };
      (COLS_BY_TAB[tab] || []).forEach((c) => {
        if (typeof saved[c.id] === "boolean") map[c.id] = saved[c.id];
      });
      return map;
    }
  } catch {
    /* ignore */
  }
  return defaults;
}

function saveCols(tab, map) {
  try {
    const raw = JSON.parse(localStorage.getItem(COLS_STORAGE) || "{}");
    const clean = {};
    (COLS_BY_TAB[tab] || []).forEach((c) => {
      clean[c.id] = map[c.id] === true;
    });
    raw[tab] = clean;
    localStorage.setItem(COLS_STORAGE, JSON.stringify(raw));
  } catch {
    /* ignore */
  }
}

function periodRange(periodId) {
  const end = moment().endOf("day").toDate();
  if (periodId === "all") {
    return { start: moment("2000-01-01").startOf("day").toDate(), end };
  }
  const p = PERIODS.find((x) => x.id === periodId) || PERIODS[2];
  if (p.days === 0) {
    return { start: moment().startOf("day").toDate(), end };
  }
  if (p.months != null) {
    return {
      start: moment().subtract(p.months, "months").startOf("day").toDate(),
      end,
    };
  }
  return { start: moment().subtract(p.days || 0, "days").startOf("day").toDate(), end };
}

function periodLabel(periodId) {
  const { start, end } = periodRange(periodId);
  if (periodId === "all") return "All History";
  if (periodId === "today") return moment(end).format("DD.MM.YYYY");
  return `${moment(start).format("DD.MM.YYYY")} – ${moment(end).format("DD.MM.YYYY")}`;
}

/** MT5-style check glyph (right side of row). */
function Mt5Check({ on }) {
  return (
    <span className={`fx-dock-ctx__check${on ? " is-on" : ""}`} aria-hidden>
      {on ? (
        <svg width="10" height="10" viewBox="0 0 10 10" focusable="false">
          <path
            d="M1.2 5.2 L3.8 7.8 L8.8 2.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}

function MenuItem({ label, checked, hasSub, active, onClick, onMouseEnter, itemRef }) {
  return (
    <button
      ref={itemRef}
      type="button"
      className={`fx-dock-ctx__item${active ? " is-active" : ""}${checked ? " is-checked" : ""}${
        hasSub ? " has-sub" : ""
      }`}
      role="menuitem"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <span className="fx-dock-ctx__label">{label}</span>
      {hasSub ? <span className="fx-dock-ctx__arrow" aria-hidden /> : <Mt5Check on={!!checked} />}
    </button>
  );
}

/**
 * MT5: flyout on the right (~2px overlap).
 * If the full list does not fit below the parent, open upward and clamp height
 * so every option stays reachable (scroll only when taller than the viewport).
 */
function placeSubmenu(anchorEl, subEl, forceUp = false) {
  if (!anchorEl || !subEl) return;
  const ar = anchorEl.getBoundingClientRect();
  const pad = 4;
  const sw = subEl.offsetWidth || 156;

  // Measure natural full height (all options)
  subEl.style.maxHeight = "none";
  const naturalH = Math.max(subEl.scrollHeight || 0, subEl.offsetHeight || 0, 120);

  let left = Math.round(ar.right - 2);
  if (left + sw > window.innerWidth - pad) {
    left = Math.max(pad, Math.round(ar.left - sw + 2));
  }

  const spaceBelow = window.innerHeight - pad - ar.top;
  const spaceAbove = ar.bottom - pad;
  const openUp = forceUp || naturalH > spaceBelow || spaceAbove >= spaceBelow;

  let maxH;
  let top;
  if (openUp) {
    // Bottom of flyout aligns with parent bottom; grow upward
    maxH = Math.min(naturalH, Math.max(88, Math.floor(spaceAbove)));
    top = Math.round(ar.bottom - maxH);
    if (top < pad) {
      top = pad;
      maxH = Math.max(88, Math.round(ar.bottom - pad));
    }
  } else {
    maxH = Math.min(naturalH, Math.max(88, Math.floor(spaceBelow)));
    top = Math.round(ar.top);
  }

  subEl.style.maxHeight = `${maxH}px`;
  subEl.style.left = `${left}px`;
  subEl.style.top = `${top}px`;
  subEl.style.overflowY = naturalH > maxH + 1 ? "auto" : "hidden";
}

export default function DockTerminal({
  mode = "trade", // trade | history
  historyTab = "deals",
  darkTheme = false,
  onModeChange,
  onHistoryTabChange,
}) {
  const rootRef = useRef(null);
  const periodItemRef = useRef(null);
  const columnsItemRef = useRef(null);
  const subRef = useRef(null);
  const [menu, setMenu] = useState(null); // { x, y, sub: null|'period'|'columns' }
  const [periodId, setPeriodId] = useState(() => {
    try {
      return localStorage.getItem(PERIOD_STORAGE) || "months3";
    } catch {
      return "months3";
    }
  });
  const [colMap, setColMap] = useState(() => loadCols(historyTab === "closed" ? "closed" : historyTab));

  const effectiveTab = historyTab === "closed" ? "closed" : historyTab;
  const range = useMemo(() => periodRange(periodId), [periodId]);

  useEffect(() => {
    setColMap(loadCols(effectiveTab));
  }, [effectiveTab]);

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (mode !== "history") setMenu(null);
  }, [mode]);

  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (e.target?.closest?.(".fx-dock-ctx")) return;
      closeMenu();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu, closeMenu]);

  useLayoutEffect(() => {
    if (!menu?.sub) return undefined;
    const anchor = menu.sub === "period" ? periodItemRef.current : columnsItemRef.current;
    // Columns always prefers up (long list near dock bottom), like MT5
    const forceUp = menu.sub === "columns";
    const place = () => placeSubmenu(anchor, subRef.current, forceUp);
    place();
    const raf = requestAnimationFrame(place);
    const onWin = () => place();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [menu, colMap, periodId, effectiveTab]);

  const onPanelContextMenu = useCallback(
    (e) => {
      if (mode !== "history") return;
      e.preventDefault();
      e.stopPropagation();
      const pad = 8;
      // Main ~122×130; leave room on the right for Columns flyout (~156)
      const mw = 122;
      const mh = 130;
      let x = e.clientX;
      let y = e.clientY;
      if (x + mw + 180 > window.innerWidth) x = Math.max(pad, window.innerWidth - mw - 180);
      if (x < pad) x = pad;
      if (y + mh > window.innerHeight - pad) y = Math.max(pad, window.innerHeight - mh - pad);
      if (y < pad) y = pad;
      setMenu({ x, y, sub: null });
    },
    [mode]
  );

  const selectHistoryView = useCallback(
    (tabId) => {
      onModeChange?.("history");
      onHistoryTabChange?.(tabId);
      closeMenu();
    },
    [onModeChange, onHistoryTabChange, closeMenu]
  );

  const selectPeriod = useCallback(
    (id) => {
      setPeriodId(id);
      try {
        localStorage.setItem(PERIOD_STORAGE, id);
      } catch {
        /* ignore */
      }
      closeMenu();
    },
    [closeMenu]
  );

  const resetCols = useCallback(() => {
    const map = defaultColMap(effectiveTab);
    setColMap(map);
    saveCols(effectiveTab, map);
  }, [effectiveTab]);

  const toggleCol = useCallback(
    (colId) => {
      setColMap((prev) => {
        const next = { ...defaultColMap(effectiveTab), ...prev, [colId]: prev[colId] === false };
        saveCols(effectiveTab, next);
        return next;
      });
    },
    [effectiveTab]
  );

  const openSub = useCallback((sub) => {
    setMenu((m) => (m ? { ...m, sub } : m));
  }, []);

  const activeHistoryId = mode === "history" ? effectiveTab : null;
  const colsDef = COLS_BY_TAB[effectiveTab] || COLS_BY_TAB.deals;

  const ctxPortal =
    menu && typeof document !== "undefined"
      ? createPortal(
          <div className={`fx-dock-ctx${darkTheme ? " fx-dock-ctx--dark" : ""}`} role="presentation">
            <div
              className="fx-dock-ctx__main"
              style={{ left: menu.x, top: menu.y }}
              role="menu"
            >
              {HISTORY_VIEWS.map((v) => (
                <MenuItem
                  key={v.id}
                  label={v.label}
                  checked={activeHistoryId === v.id}
                  onClick={() => selectHistoryView(v.id)}
                  onMouseEnter={() => openSub(null)}
                />
              ))}
              <div className="fx-dock-ctx__sep" />
              <MenuItem
                itemRef={periodItemRef}
                label="Period"
                hasSub
                active={menu.sub === "period"}
                onMouseEnter={() => openSub("period")}
                onClick={() => openSub("period")}
              />
              <div className="fx-dock-ctx__sep" />
              <MenuItem
                itemRef={columnsItemRef}
                label="Columns"
                hasSub
                active={menu.sub === "columns"}
                onMouseEnter={() => openSub("columns")}
                onClick={() => openSub("columns")}
              />
            </div>

            {menu.sub === "period" ? (
              <div ref={subRef} className="fx-dock-ctx__sub fx-dock-ctx__sub--period" role="menu">
                {PERIODS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`fx-dock-ctx__item fx-dock-ctx__item--period${
                      periodId === p.id ? " is-checked" : ""
                    }`}
                    onClick={() => selectPeriod(p.id)}
                  >
                    <span className="fx-dock-ctx__period-label">{p.label}</span>
                    <span className="fx-dock-ctx__period-range">
                      {p.id === "all" ? "" : periodLabel(p.id)}
                    </span>
                    <Mt5Check on={periodId === p.id} />
                  </button>
                ))}
              </div>
            ) : null}

            {menu.sub === "columns" ? (
              <div ref={subRef} className="fx-dock-ctx__sub fx-dock-ctx__sub--columns" role="menu">
                {colsDef.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`fx-dock-ctx__item${colMap[c.id] === true ? " is-checked" : ""}`}
                    onClick={() => toggleCol(c.id)}
                  >
                    <span className="fx-dock-ctx__label">{c.label}</span>
                    <Mt5Check on={colMap[c.id] === true} />
                  </button>
                ))}
                <div className="fx-dock-ctx__sep" />
                <button type="button" className="fx-dock-ctx__item" onClick={resetCols}>
                  <span className="fx-dock-ctx__label">Reset all</span>
                  <Mt5Check on={false} />
                </button>
              </div>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`fx-dock-terminal${darkTheme ? " fx-dock-terminal--dark" : ""}`}
      onContextMenu={onPanelContextMenu}
    >
      {mode === "history" ? (
        <History
          embedded
          mt5
          initialTab={effectiveTab}
          startDate={range.start}
          endDate={range.end}
          visibleColumns={colMap}
          darkTheme={darkTheme}
        />
      ) : (
        <PositionReport embedded hideFilters darkTheme={darkTheme} />
      )}
      {ctxPortal}
    </div>
  );
}
